import { useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Twitter } from "lucide-react";
import { toast } from "sonner";
import { Divider } from "./Primitives";
import { useContent } from "../context/ContentContext";
import { postSubmission } from "../lib/api";

const QUICK = [
  { label: "About Us", to: "/about" },
  { label: "Areas of Focus", to: "/areas-of-focus" },
  { label: "Events", to: "/events" },
  { label: "Publications", to: "/publications" },
  { label: "Advisory Board", to: "/advisory-board" },
  { label: "Contact", to: "/contact" },
];

export const Footer = () => {
  const { t } = useContent();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await postSubmission({
        type: "newsletter",
        name: "Newsletter Subscriber",
        email,
        message: "Newsletter signup",
      });
      toast.success("Thanks for subscribing!");
      setEmail("");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
  <footer data-testid="footer" className="bg-amf-footer text-amf-cream pt-16 md:pt-20 pb-10">
    <div className="max-w-[1200px] mx-auto px-6 md:px-10">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
        <div>
          <img
            src="/board/logo-circle.png"
            alt="Asana Memorial Foundation logo"
            className="h-16 w-16 rounded-full object-cover mb-4"
          />
          <h3 className="font-playfair text-2xl text-amf-cream">
            Asana Memorial Foundation
          </h3>
          <p className="font-playfair italic text-amf-gold mt-3 text-lg">
            {t("global.footer.tagline")}
          </p>
          <p className="font-lora text-amf-cream/70 mt-4 leading-relaxed text-sm max-w-xs">
            {t("global.footer.blurb")}
          </p>
        </div>

        <div>
          <p className="amf-overline text-amf-gold mb-5">Quick Links</p>
          <ul className="space-y-3">
            {QUICK.map((q) => (
              <li key={q.to}>
                <Link
                  to={q.to}
                  className="font-lora text-amf-cream/80 hover:text-amf-gold transition-colors"
                >
                  {q.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="amf-overline text-amf-gold mb-5">Contact Us</p>
          <ul className="space-y-3 font-lora text-amf-cream/80 text-sm">
            <li>{t("global.contact.phone")}</li>
            <li>
              <a href={`mailto:${t("global.contact.email")}`} className="hover:text-amf-gold transition-colors break-all">
                {t("global.contact.email")}
              </a>
            </li>
            <li className="leading-relaxed">{t("global.contact.address")}</li>
          </ul>
          <div className="flex items-center gap-4 mt-6">
            {[Facebook, Instagram, Linkedin, Twitter].map((Icon, i) => (
              <a
                key={i}
                href="#"
                aria-label="Social media"
                className="text-amf-cream/70 hover:text-amf-gold transition-colors"
              >
                <Icon size={20} strokeWidth={1.5} />
              </a>
            ))}
          </div>
        </div>

        <div>
          <p className="amf-overline text-amf-gold mb-5">Newsletter</p>
          <p className="font-lora text-amf-cream/70 text-sm leading-relaxed mb-4">
            Stay updated with our latest news and impact stories.
          </p>
          <form onSubmit={handleSubscribe} className="flex flex-col gap-3" data-testid="newsletter-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              data-testid="newsletter-email"
              className="w-full bg-white/10 border border-white/25 text-amf-cream placeholder-amf-cream/50 px-4 py-2.5 font-lora text-sm focus:outline-none focus:border-amf-gold transition-colors"
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="newsletter-submit"
              className="amf-overline bg-amf-burgundy text-amf-cream px-5 py-2.5 hover:bg-[var(--amf-accent-dark)] transition-colors disabled:opacity-60"
            >
              {sending ? "Subscribing…" : "Subscribe"}
            </button>
          </form>
        </div>
      </div>

      <Divider className="my-10 opacity-20" />
      <p className="amf-overline text-amf-stone text-center tracking-[0.15em]">
        {t("global.footer.copyright")}
      </p>
    </div>
  </footer>
  );
};
